import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineSparkles,
  HiOutlineTableCells,
  HiOutlineBolt,
  HiOutlineRocketLaunch,
  HiOutlineCheck,
} from 'react-icons/hi2';

// Shown when a free-plan workspace tries to connect an AI agent. Connecting
// Claude / any MCP client is a paid feature (backend gates token creation and
// OAuth consent with 402 PLAN_REQUIRED); this sells the feature and routes to
// /plans rather than letting the user hit that wall after filling in a form.

const PERKS = [
  {
    icon: HiOutlineTableCells,
    text: 'Query and clean your scraped tables in plain English',
  },
  {
    icon: HiOutlineBolt,
    text: 'Run and monitor your automations from the conversation',
  },
  {
    icon: HiOutlineRocketLaunch,
    text: 'Launch new extractions — credits always confirmed first',
  },
];

const McpUpsellModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Rendered into <body>: embedders like the Integrations MCP card sit inside
  // `backdrop-blur` containers, and a filtered ancestor becomes the containing
  // block for `position: fixed` — which would trap the overlay inside the card
  // instead of covering the viewport (same fix as ConnectClaudeModal).
  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in duration-200 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent, echoing the AI & MCP page's brand signature */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" aria-hidden="true" />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3.5 right-3.5 z-10 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-6 pt-8 pb-6 text-center">
          {/* Icon */}
          <div className="relative w-14 h-14 mx-auto mb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/30 via-cyan-400/30 to-purple-400/30 blur-xl rounded-full" aria-hidden="true" />
            <div className="relative w-14 h-14 rounded-2xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20">
              <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[14px] flex items-center justify-center">
                <HiOutlineSparkles className="w-6 h-6 text-cyan-500 dark:text-cyan-400" />
              </div>
            </div>
          </div>

          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/30 dark:border-cyan-400/30 text-cyan-600 dark:text-cyan-300 mb-4">
            Pro feature
          </span>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight mb-2">
            Connect AI agents to your workspace
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs mx-auto">
            Connecting Claude — or any MCP client — is available on any paid plan. Upgrade to chat
            with your scraped data.
          </p>
        </div>

        {/* Perks */}
        <div className="px-6 pb-2 space-y-2.5">
          {PERKS.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.05]">
              <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 flex items-center justify-center">
                <Icon className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{text}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 pt-4 pb-6 space-y-2.5">
          <button
            onClick={() => navigate('/plans')}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            <HiOutlineCheck className="w-4 h-4" strokeWidth={2.5} />
            See plans
          </button>
          <button
            onClick={onClose}
            className="w-full px-5 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-xl transition-all duration-200"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default McpUpsellModal;
